import { createAsyncThunk } from "@reduxjs/toolkit";

import { type AsyncThunkCreator } from "../../../index";
import { TEAM_REDUCER_NAME } from "../types";

interface Payload {
  memberId: string;
  productIds: string[];
}

/**
 * Replaces the member's product scope. Written as a diff rather than
 * delete-all-then-insert so a failed insert cannot leave the member with no
 * products at all — RLS rejects a product outside the tenant, and that rejection
 * must not also wipe the links that were already correct.
 */
export const setTeamMemberProductScope = createAsyncThunk<void, Payload, AsyncThunkCreator<string>>(
  `${TEAM_REDUCER_NAME}/setTeamMemberProductScope`,
  async ({ memberId, productIds }, { rejectWithValue, extra: { db } }) => {
    const { data: currentRows, error: currentError } = await db
      .from("team_member_product")
      .select("productId")
      .eq("memberId", memberId);

    if (currentError) {
      return rejectWithValue(currentError.message);
    }

    const current = new Set((currentRows ?? []).map((row) => row.productId));
    const next = new Set(productIds);
    const added = productIds.filter((productId) => !current.has(productId));
    const removed = [...current].filter((productId) => !next.has(productId));

    if (added.length) {
      const { error } = await db
        .from("team_member_product")
        .insert(added.map((productId) => ({ memberId, productId })));

      if (error) {
        return rejectWithValue(error.message);
      }
    }

    if (removed.length) {
      const { error } = await db
        .from("team_member_product")
        .delete()
        .eq("memberId", memberId)
        .in("productId", removed);

      if (error) {
        return rejectWithValue(error.message);
      }
    }
  },
);
